
import React, { useState, useEffect } from 'react';
import { useTutorial, TutorialStep } from '@/contexts/TutorialContext';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  HelpCircle, 
  Lightbulb,
  Save,
  FilePlus,
  RotateCcw,
  RotateCw,
  Copy,
  Trash,
  Move,
  Maximize2,
  FlipHorizontal,
  FlipVertical,
  LayoutDashboard,
  BookOpen,
  BellDot,
  User,
  MessageSquarePlus,
  ThumbsUp,
  Pencil,
  Award,
  BarChart,
  UserCircle,
  Plus,
  Minus,
  Expand,
  Lock,
  LockOpen,
  FolderOpen
} from 'lucide-react';

const EditButtons = () => {
  return (
    <div className="flex flex-col items-center mt-4 space-y-6">
      <div className="grid grid-cols-3 gap-6">
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <RotateCcw className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Linksom</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <RotateCw className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Rechtsom</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <Copy className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Kopiëren</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <Trash className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Verwijderen</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <FlipHorizontal className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Horizontaal</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <FlipVertical className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Verticaal</span>
        </div>
      </div>
    </div>
  );
};

const SaveButtons = () => {
  return (
    <div className="flex flex-col items-center mt-4 space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <Save className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Opslaan</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <FilePlus className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Opslaan als</span>
        </div>
      </div>
    </div>
  );
};

const ZoomLockButtons = () => {
  return (
    <div className="flex flex-col items-center mt-4 space-y-6">
      <div className="grid grid-cols-4 gap-6">
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <Plus className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Inzoomen</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <Minus className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Uitzoomen</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <Expand className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Passend</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-2 border rounded-md bg-gray-50 mb-2 w-12 h-12 flex items-center justify-center">
            <Lock className="h-5 w-5 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600">Vergrendelen</span>
        </div>
      </div>
    </div>
  );
};

const DashboardFeatures = () => {
  return (
    <div className="flex justify-center mt-4 gap-6">
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <FolderOpen className="h-5 w-5 text-gray-600" />
        </div>
        <span className="text-xs text-gray-600">Openen</span>
      </div>
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <Pencil className="h-5 w-5 text-gray-600" />
        </div>
        <span className="text-xs text-gray-600">Bewerken</span>
      </div>
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <Copy className="h-5 w-5 text-gray-600" />
        </div>
        <span className="text-xs text-gray-600">Dupliceren</span>
      </div>
    </div>
  );
};

const UpdatesFeatures = () => {
  return (
    <div className="flex justify-center mt-4 gap-6">
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <BellDot className="h-5 w-5 text-blue-500" />
        </div>
        <span className="text-xs text-gray-600">Updates</span>
      </div>
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <MessageSquarePlus className="h-5 w-5 text-gray-600" />
        </div>
        <span className="text-xs text-gray-600">Feedback</span>
      </div>
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <ThumbsUp className="h-5 w-5 text-green-500" />
        </div>
        <span className="text-xs text-gray-600">Stemmen</span>
      </div>
    </div>
  );
};

const ProfileFeatures = () => {
  return (
    <div className="flex justify-center mt-4 gap-6">
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <UserCircle className="h-5 w-5 text-gray-600" />
        </div>
        <span className="text-xs text-gray-600">Gegevens</span>
      </div>
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <Award className="h-5 w-5 text-blue-500" />
        </div>
        <span className="text-xs text-gray-600">Badges</span>
      </div>
      <div className="flex flex-col items-center">
        <div className="p-2 border rounded-md bg-gray-50 mb-2">
          <BarChart className="h-5 w-5 text-green-500" />
        </div>
        <span className="text-xs text-gray-600">Statistieken</span>
      </div>
    </div>
  );
};

export default function TutorialDialog() {
  const { 
    isActive, 
    currentStep, 
    totalSteps, 
    currentStepData, 
    nextStep, 
    prevStep, 
    endTutorial,
    shouldNeverShowAgain,
    setShouldNeverShowAgain
  } = useTutorial();
  
  const [targetPosition, setTargetPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  useEffect(() => {
    if (isActive && currentStepData?.target) {
      const targetElement = document.querySelector(currentStepData.target);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setTargetPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
      } else {
        setTargetPosition(null);
      }
    } else {
      setTargetPosition(null);
    }
  }, [isActive, currentStepData]);

  if (!isActive) return null;

  const renderStepIndicator = () => {
    return (
      <div className="flex justify-center gap-1 my-2">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div 
            key={index} 
            className={`h-2 w-2 rounded-full ${currentStep === index + 1 
              ? 'bg-primary' 
              : 'bg-muted-foreground/30'}`} 
          />
        ))}
      </div>
    );
  };

  const renderStepIcon = () => {
    switch (currentStep) {
      case 4: 
        return <Save className="h-5 w-5 text-primary" />;
      case 5: 
        return <LayoutDashboard className="h-5 w-5 text-primary" />;
      case 6: 
        return <BookOpen className="h-5 w-5 text-primary" />;
      case 7: 
        return <BellDot className="h-5 w-5 text-primary" />;
      case 8: 
        return <User className="h-5 w-5 text-primary" />;
      default: 
        return <Lightbulb className="h-5 w-5 text-yellow-500" />;
    }
  };

  const renderFeatureExplanation = () => {
    switch (currentStep) {
      case 3:
        return <EditButtons />;
      case 4:
        return (
          <>
            <SaveButtons />
            <ZoomLockButtons />
          </>
        );
      case 5:
        return <DashboardFeatures />;
      case 6:
        return null;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isActive} onOpenChange={(open) => !open && endTutorial()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {renderStepIcon()} 
            {currentStepData?.title}
          </DialogTitle>
          <DialogDescription>
            {currentStepData?.description}
          </DialogDescription>
        </DialogHeader>
        
        {currentStepData?.image ? (
          <div className="flex flex-col items-center justify-center my-4">
            {currentStep !== 8 ? (
              <div className="rounded-md overflow-hidden border border-border">
                <img 
                  src={currentStepData.image} 
                  alt={currentStepData.title}
                  className="max-w-full h-auto object-contain"
                />
              </div>
            ) : (
              <div className="overflow-hidden">
                <img 
                  src={currentStepData.image} 
                  alt={currentStepData.title}
                  className="max-w-full h-auto object-contain"
                />
              </div>
            )}
          </div>
        ) : null}
        
        {renderFeatureExplanation()}
        
        {renderStepIndicator()}
        
        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-4 items-center">
          <div className="flex items-center gap-2">
            <Checkbox 
              id="never-show-again" 
              checked={shouldNeverShowAgain} 
              onCheckedChange={(checked) => setShouldNeverShowAgain(checked === true)}
            />
            <label 
              htmlFor="never-show-again" 
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Niet meer tonen
            </label>
          </div>
          
          <div className="flex gap-2">
            {currentStep > 1 ? (
              <Button variant="outline" onClick={prevStep} size="sm">
                <ChevronLeft className="mr-1 h-4 w-4" /> Vorige
              </Button>
            ) : (
              <Button variant="outline" onClick={endTutorial} size="sm">
                Overslaan
              </Button>
            )}
            
            <Button onClick={nextStep} size="sm">
              {currentStep < totalSteps ? (
                <>Volgende <ChevronRight className="ml-1 h-4 w-4" /></>
              ) : (
                'Afronden'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      
      {targetPosition && (
        <div 
          className="fixed pointer-events-none border-2 border-primary animate-pulse rounded-sm z-50"
          style={{
            top: targetPosition.top - 4 + 'px',
            left: targetPosition.left - 4 + 'px',
            width: targetPosition.width + 8 + 'px',
            height: targetPosition.height + 8 + 'px',
          }}
        />
      )}
    </Dialog>
  );
}
